import { FC } from "react";
import { useSelector } from "react-redux";
import { GetProgramFeeResponse } from "../../../../../../../../api/services/common/models";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiViewMorePopOver,
} from "../../../../../../../../components";
import { BillingTypeConfigureTable } from "../../../../../../../../components/billingTypeConfigureTable/BillingTypeConfigureTable";
import {
  CURRENCY_FORMAT_MAP,
  CurrencyFormat,
} from "../../../../../../../../components/textFieldCurrency/CurrencyUtils";
import {
  gridSize,
  viewMorePopOverGridSize,
} from "../../../../../../../../helpers/constants";
import {
  getCurrencyNameAndLocaleById,
  getFormattedAmount,
  getValueOrBlank,
  getValueOrDash,
  isActivityBasedBilling,
  isEmpty,
  isMilestoneBilling,
  isScheduledBasedBilling,
} from "../../../../../../../../helpers/utils";
import { useForceUpdate } from "../../../../../../../../hooks/useForceUpdate";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { currencyListSelector } from "../../../../../../../../redux/slices/appState/appStateSelector";
import {
  billingConfigurationDetailsFormKeys,
  ProgramDetail,
} from "../../../../../../../createProgram/modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../../createProgram/siteCore/SiteCoreKeys";
import { ViewProgramFeeWrapper } from "./ViewProgramFee.style";

interface ViewProgramFeeProps {
  programFeeDetailsData: GetProgramFeeResponse;
  programData?: ProgramDetail;
  pageId: string;
}

export const ViewProgramFee: FC<ViewProgramFeeProps> = ({
  programFeeDetailsData,
  programData,
  pageId,
}) => {
  const { commonLabels, fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();

  const currencyList = useSelector(currencyListSelector);

  const forceUpdate = useForceUpdate();
  const {
    programFeeTitle,
    earlyPayDays,
    earlyPayPercentage,
    minimumTransferFee,
    billingTrigger,
    currency,
    programFee,
    billingTriggerList,
    billingTypeList,
    billingType: billingTypeSiteCore,
    proBonoCategory,
    proBonoCategoryList,
    specialBillingInstructions,
  } = programSiteCoreKeys;

  const getLabel = (key: string) => {
    return fetchLabel(programFeeTitle, key);
  };

  const shouldShowConfigurationTable = () => {
    return (
      programFeeDetailsData?.billingTypeId &&
      (isScheduledBasedBilling(programFeeDetailsData?.billingTypeId) ||
        isMilestoneBilling(programFeeDetailsData?.billingTypeId))
    );
  };

  const getDropdownValues = (key: string, value?: string) => {
    if (!value || isEmpty(value)) return "";
    const data = fetchValueByKeyOfDropdown(programFeeTitle, key, value);

    return data;
  };

  const getEarlyPayPercentage = () => {
    if (
      !programFeeDetailsData?.earlyPayPercentage ||
      isEmpty(programFeeDetailsData?.earlyPayPercentage)
    ) {
      return "";
    }
    return `${programFeeDetailsData?.earlyPayPercentage}%`;
  };

  const getEarlyPayDays = () => {
    if (
      !programFeeDetailsData?.earlyPayDays ||
      isEmpty(programFeeDetailsData?.earlyPayDays)
    ) {
      return "";
    }
    return `${programFeeDetailsData?.earlyPayDays} ${programFeeDetailsData?.earlyPayDays > 1 ? commonLabels.days : commonLabels.day}`;
  };

  const getCurrencyName = () => {
    return getValueOrBlank(programFeeDetailsData?.currency?.name);
  };

  const getCurrencyFormat = () => {
    const currencyFormat: CurrencyFormat =
      CURRENCY_FORMAT_MAP[getCurrencyName()];
    return currencyFormat ?? "";
  };

  const getFormattedValue = (value: number): string => {
    const currencyData = getCurrencyNameAndLocaleById(
      currencyList,
      programFeeDetailsData?.currency?.id,
    );

    const formattedAmount = `${getCurrencyName()} ${getFormattedAmount(value, currencyData?.currencyLocale)}`;
    return formattedAmount;
  };

  const getProgramFee = () => {
    if (!programFeeDetailsData?.fee || isEmpty(programFeeDetailsData?.fee)) {
      return "";
    }
    return getFormattedValue(programFeeDetailsData?.fee);
  };

  const getMinimumTransferFee = () => {
    if (
      !programFeeDetailsData?.minimumTransferFee ||
      isEmpty(programFeeDetailsData?.minimumTransferFee)
    ) {
      return "";
    }
    return getFormattedValue(programFeeDetailsData?.minimumTransferFee);
  };

  return (
    <ViewProgramFeeWrapper>
      <MuiBox className="content">
        <MuiGrid container spacing={2}>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getLabel(billingTypeSiteCore)}
              data-testid="billing-type-label"
              replaceEmptyWithDash
              value={getDropdownValues(
                billingTypeList,
                programFeeDetailsData?.billingTypeId,
              )}
            />
          </MuiGrid>

          {isActivityBasedBilling(programFeeDetailsData?.billingTypeId) && (
            <>
              <MuiGrid size={gridSize}>
                <MuiDoubleLabel
                  data-testid="billing-trigger-label"
                  label={getLabel(billingTrigger)}
                  replaceEmptyWithDash
                  value={getDropdownValues(
                    billingTriggerList,
                    programFeeDetailsData?.billingTriggerId,
                  )}
                />
              </MuiGrid>

              <MuiGrid size={gridSize} className="form-field-normal" />
            </>
          )}

          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              data-testid="currency-label"
              label={getLabel(currency)}
              replaceEmptyWithDash
              value={getCurrencyName()}
            />
          </MuiGrid>

          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              data-testid="program-fee-label"
              label={getLabel(programFee)}
              replaceEmptyWithDash
              value={getProgramFee()}
            />
          </MuiGrid>

          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              data-testid="minimum-transfer-fee-label"
              label={getLabel(minimumTransferFee)}
              replaceEmptyWithDash
              value={getMinimumTransferFee()}
            />
          </MuiGrid>

          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              data-testid="early-pay-days-label"
              label={getLabel(earlyPayDays)}
              replaceEmptyWithDash
              value={getEarlyPayDays()}
            />
          </MuiGrid>

          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              data-testid="early-pay-percentage-label"
              label={getLabel(earlyPayPercentage)}
              replaceEmptyWithDash
              value={getEarlyPayPercentage()}
            />
          </MuiGrid>

          {isActivityBasedBilling(programFeeDetailsData?.billingTypeId) && (
            <MuiGrid size={gridSize} className="form-field-normal" />
          )}

          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              // data-testid="pro-bono-category-label"
              label={getLabel(proBonoCategory)}
              replaceEmptyWithDash
              value={getDropdownValues(
                proBonoCategoryList,
                programFeeDetailsData?.proBonoCategoryId,
              )}
            />
          </MuiGrid>
        </MuiGrid>
        <MuiGrid container spacing={2} className="special-instructions-grid">
          <MuiGrid size={viewMorePopOverGridSize}>
            <MuiDoubleLabel
              label={getLabel(specialBillingInstructions)}
              replaceEmptyWithDash
              value={
                programFeeDetailsData?.specialBillingInstructions ? (
                  <MuiViewMorePopOver
                    anchorText={commonLabels.viewMore}
                    message={getValueOrDash(
                      programFeeDetailsData?.specialBillingInstructions,
                    )}
                    placement="bottom-end"
                  />
                ) : (
                  ""
                )
              }
            />
          </MuiGrid>
        </MuiGrid>

        {shouldShowConfigurationTable() && (
          <BillingTypeConfigureTable
            pageId={pageId}
            data-testid="billing-config-table"
            currencyName={getCurrencyName()}
            currencyFormat={getCurrencyFormat()}
            currencyId={programFeeDetailsData?.currency?.id}
            forceUpdate={forceUpdate}
            programTemplateId={getValueOrBlank(programData?.programTemplateId)}
            isFromBillingInvoice={true}
            billingConfigurationDetailsFormKeys={
              billingConfigurationDetailsFormKeys
            }
            clientId={programData?.clientId!}
            isViewOnly={true}
          />
        )}
      </MuiBox>
    </ViewProgramFeeWrapper>
  );
};
